import { Box } from "arkh";
import { StorecastPickerButton } from "./StorecastPickerButton";
import { StorecastState } from "../types";
import React, { PropsWithChildren, useCallback, useRef } from "react";
import { useStorecastHeaderLayout } from "./useStorecastHeaderLayout";
import { useIntlTokens } from "../../../../components/mobile/useIntlTokens";
import { Insets, Platform } from "react-native";
import { StorecastHeaderButton } from "./StorecastHeaderButton";
import { UserLoggedInNavigatorNavigationProp } from "../../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { graphql, useFragment } from "react-relay";
import { OrganisationHeader } from "../../../../components/OrgHeaders/OrganisationHeader";
import { StorecastHeader_store$key } from "./__generated__/StorecastHeader_store.graphql";
import { StorecastHeader_dashboard$key } from "./__generated__/StorecastHeader_dashboard.graphql";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { useSizeClass } from "../../../../common/Utils/useSizeClass";
import { SizeClass } from "@/src/common/Utils/SizeClass";
import { StorecastAdminButton } from "./StorecastAdminButton";
import { StorecastCloseButton } from "./StorecastCloseButton";
import { StorecastMoreButton } from "./StorecastMoreButton";
import {
  StorecastFocusBottomSheet,
  StorecastFocusBottomSheetRef,
} from "../StorecastFocusBottomSheet";
import { StorecastShowFileButton } from "./StorecastShowFileButton";
import { HeaderProminentButton } from "./HeaderProminentButton";
import { StorecastFocusEndButton } from "./StorecastFocusEndButton";
import { StatusBar } from "expo-status-bar";
import { usePersistedDashboardId } from "../../../../OS/Login/State";
import { TeamHeaderProfileButton } from "@/src/features/Team/TeamHeader/TeamHeaderProfileButton";

function StorecastHeaderContainer(
  props: PropsWithChildren<{
    insets: Insets;
  }>
) {
  const { height } = useStorecastHeaderLayout();
  return (
    <Box
      insets={props.insets}
      style={{
        height: height + (props.insets?.top ?? 0),
        flexDirection: "row",
        justifyContent: "flex-end",
        alignItems: "center",
        width: "100%",
      }}
      spaceHorizontal="small"
    >
      {props.children}
    </Box>
  );
}

export function StorecastHeaderLoading(props: {
  onClose(): void;
  onChangeDashboard(dashboard: { id: string }): void;
  navigation: UserLoggedInNavigatorNavigationProp<"Dashboard">;
  hasOrg: boolean;
  asHome: boolean;
  storeName: string;
}) {
  const insets = useSafeAreaInsets();
  const hasOrgHeader = props.hasOrg && props.asHome;
  return (
    <Box color="surface-primary">
      <StatusBar style="dark" />
      {hasOrgHeader ? (
        <OrganisationHeader
          navigateDeprecatedToOrg={() =>
            props.navigation.navigateDeprecated("OrgHome", {})
          }
          storeName={props.storeName}
        />
      ) : null}
      <StorecastHeaderContainer insets={insets}>
        <StorecastCloseButton onPress={props.onClose} />
      </StorecastHeaderContainer>
    </Box>
  );
}

type StorecastHeaderProps = {
  store: StorecastHeader_store$key;
  dashboard: StorecastHeader_dashboard$key | null;
  navigation: UserLoggedInNavigatorNavigationProp<"Dashboard">;
  hasOrg: boolean;
  asHome: boolean;
  state: StorecastState;
  setStorecastState: (state: StorecastState) => void;
};

export const StorecastHeader = React.memo(StorecastHeaderComponent);

function StorecastHeaderComponent(props: StorecastHeaderProps) {
  const persistedDashboard = usePersistedDashboardId();
  const isCompact = useSizeClass().width === SizeClass.COMPACT;
  const insets = useSafeAreaInsets();
  const store = useFragment(
    graphql`
      fragment StorecastHeader_store on Store {
        myRole
        name
        dashboards {
          id
          ...StorecastPickerButton_dasbhoards
        }

        currentEmployee(id: $employeeId) {
          id
          ...TeamHeaderProfileButton_employee
        }
        me {
          ...TeamHeaderProfileButton_storeUser
        }
        ...StorecastAdminButton_Store
      }
    `,
    props.store
  );

  const dashboard = useFragment(
    graphql`
      fragment StorecastHeader_dashboard on Dashboard {
        id
        ...StorecastAdminButton_Dashboard
        ...StorecastPickerButton_dashboard
      }
    `,
    props.dashboard
  );

  const { navigation, setStorecastState } = props;

  const intl_tokens = useIntlTokens();
  const hasAdmin =
    store && (store.myRole === "ADMIN" || store.myRole === "MANAGER");
  const hasOrgHeader = props.hasOrg && props.asHome;

  const onPressEditLayoutAndWidgets = useCallback(
    () => setStorecastState("EDIT"),
    [setStorecastState]
  );
  const onChangeStorecast = useCallback(
    (storecast: { id: string }) => {
      navigation.setParams({
        dashboardId: storecast.id,
      });
      persistedDashboard.setCurrentDashboard(storecast.id);
    },
    [navigation, persistedDashboard]
  );

  const focusModeStartBottomSheetRef =
    useRef<StorecastFocusBottomSheetRef>(null);

  const onPressFocusButton = useCallback(() => {
    if (dashboard?.id) {
      focusModeStartBottomSheetRef.current?.open(dashboard.id);
      setTimeout(() => {
        setStorecastState("FOCUS");
      }, 100);
    }
  }, [setStorecastState, dashboard]);

  return (
    <>
      <Box color="surface-primary">
        <StatusBar style="dark" />
        {hasOrgHeader ? (
          <OrganisationHeader
            navigateDeprecatedToOrg={() =>
              props.navigation.navigateDeprecated("OrgHome", {})
            }
            storeName={store.name}
          />
        ) : null}
        <StorecastHeaderContainer
          insets={{ top: hasOrgHeader ? 0 : insets.top }}
        >
          {props.state === "FOCUS" ? (
            <>
              {dashboard ? (
                <StorecastShowFileButton
                  dashboard={dashboard}
                  navigation={props.navigation}
                />
              ) : null}
              {Platform.OS !== "web" && dashboard ? (
                <StorecastFocusEndButton
                  dashboard={dashboard}
                  setStorecastState={props.setStorecastState}
                />
              ) : null}
            </>
          ) : null}
          {props.state === "IDLE" ? (
            <>
              <Box
                style={{ flex: 1, flexDirection: "row", alignItems: "center" }}
              >
                {!props.asHome ? (
                  <Box>
                    <StorecastCloseButton
                      onPress={() => {
                        props.navigation.goBack();
                      }}
                    />
                  </Box>
                ) : null}
                {dashboard ? (
                  <StorecastPickerButton
                    currentDashboard={dashboard}
                    onChangeDashboard={
                      store.dashboards.length > 1
                        ? onChangeStorecast
                        : undefined
                    }
                    isCompact={isCompact}
                    dashboards={store.dashboards}
                  />
                ) : null}
              </Box>
              {props.asHome ? (
                <StorecastMoreButton
                  navigation={props.navigation}
                  isCompact={isCompact}
                />
              ) : null}
              {hasAdmin ? (
                <StorecastAdminButton
                  dashboard={dashboard ?? null}
                  onPressEditLayoutAndWidgets={onPressEditLayoutAndWidgets}
                  navigation={props.navigation}
                  store={store}
                  isCompact={isCompact}
                />
              ) : null}
              {Platform.OS !== "web" && dashboard ? (
                <StorecastHeaderButton
                  icon="focus-mode"
                  onPress={onPressFocusButton}
                  title={isCompact ? undefined : intl_tokens.FocusMode}
                />
              ) : null}

              {!props.asHome || hasOrgHeader ? null : (
                <TeamHeaderProfileButton
                  user={store.me ?? null}
                  employee={store.currentEmployee ?? null}
                  onPress={() => {
                    props.navigation.navigate("Profile", {});
                  }}
                  compact={isCompact}
                />
              )}
            </>
          ) : null}
          {props.state === "EDIT" ? (
            <Box spaceLeft="small">
              <HeaderProminentButton
                title={intl_tokens.Done}
                onPress={() => {
                  props.setStorecastState("IDLE");
                }}
              />
            </Box>
          ) : null}
        </StorecastHeaderContainer>
      </Box>
      {dashboard ? (
        <StorecastFocusBottomSheet
          ref={focusModeStartBottomSheetRef}
          dashboard={dashboard}
          setStorecastState={props.setStorecastState}
        />
      ) : null}
    </>
  );
}
